'use client'

import * as React from 'react'
import { useTranslations } from 'next-intl'
import { Crown, RotateCcw, Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { AppearanceContext, ChampionIcon } from './champion-icon'
import {
  DEFAULT_APPEARANCE,
  type BracketAppearance,
  type ChampionIconKind,
} from './appearance'

async function prepareIcon(file: File): Promise<string> {
  if (
    !['image/png', 'image/jpeg', 'image/webp'].includes(file.type) ||
    file.size > 5 * 1024 * 1024
  ) {
    throw new Error('invalid-file')
  }
  const url = URL.createObjectURL(file)
  try {
    const image = new Image()
    image.src = url
    await image.decode()
    const scale = Math.min(
      1,
      512 / Math.max(image.naturalWidth, image.naturalHeight),
    )
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale))
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale))
    const context = canvas.getContext('2d')
    if (!context) throw new Error('canvas-unavailable')
    context.drawImage(image, 0, 0, canvas.width, canvas.height)
    return canvas.toDataURL('image/webp', 0.9)
  } finally {
    URL.revokeObjectURL(url)
  }
}

function ColorControl({
  label,
  value,
  fallback,
  onChange,
}: {
  label: string
  value: string | null
  fallback: string
  onChange: (value: string) => void
}) {
  const t = useTranslations('maker.appearance')
  return (
    <label className="flex items-center justify-between gap-3 text-sm">
      <span>{label}</span>
      <span className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">
          {value ?? t('automatic')}
        </span>
        <input
          type="color"
          value={value ?? fallback}
          onChange={(event) => onChange(event.target.value)}
          className="h-9 w-12 cursor-pointer rounded border bg-background p-1"
        />
      </span>
    </label>
  )
}

export function AppearanceControls({
  appearance,
  onChange,
  storageError,
}: {
  appearance: BracketAppearance
  onChange: (patch: Partial<BracketAppearance>) => void
  storageError: boolean
}) {
  const t = useTranslations('maker.appearance')
  const inputRef = React.useRef<HTMLInputElement>(null)
  const uploadVersion = React.useRef(0)
  const [uploading, setUploading] = React.useState(false)
  const [error, setError] = React.useState(false)
  React.useEffect(
    () => () => {
      uploadVersion.current++
    },
    [],
  )
  const selectIcon = (icon: ChampionIconKind) => {
    uploadVersion.current++
    setUploading(false)
    onChange({ icon })
  }
  return (
    <div className="space-y-6">
      <section className="space-y-4" aria-label={t('championTitle')}>
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <Crown className="h-4 w-4" />
          {t('championTitle')}
        </h3>
        <div className="flex min-h-28 items-center gap-4 rounded-md border bg-muted/20 p-4">
          <div
            className="flex h-20 w-20 shrink-0 items-center justify-center rounded-md border bg-background"
            aria-hidden
          >
            <AppearanceContext.Provider value={appearance}>
              <ChampionIcon />
            </AppearanceContext.Provider>
          </div>
          <p className="text-xs leading-relaxed text-muted-foreground">
            {t('championHint')}
          </p>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {(['crown', 'trophy', 'star', 'none'] as const).map((icon) => (
            <Button
              key={icon}
              type="button"
              variant={appearance.icon === icon ? 'secondary' : 'outline'}
              aria-pressed={appearance.icon === icon}
              className="px-1 text-xs"
              onClick={() => selectIcon(icon)}
            >
              {t(icon)}
            </Button>
          ))}
        </div>
        {appearance.customIcon && (
          <Button
            type="button"
            variant={appearance.icon === 'custom' ? 'secondary' : 'outline'}
            aria-pressed={appearance.icon === 'custom'}
            className="w-full"
            onClick={() => selectIcon('custom')}
          >
            {t('useUpload')}
          </Button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          aria-label={t('upload')}
          onChange={async (event) => {
            const file = event.currentTarget.files?.[0]
            event.currentTarget.value = ''
            if (!file) return
            const version = ++uploadVersion.current
            setUploading(true)
            setError(false)
            try {
              const customIcon = await prepareIcon(file)
              if (version === uploadVersion.current)
                onChange({ customIcon, icon: 'custom' })
            } catch {
              if (version === uploadVersion.current) setError(true)
            } finally {
              if (version === uploadVersion.current) setUploading(false)
            }
          }}
        />
        <Button
          type="button"
          variant="outline"
          className="w-full gap-2"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          <Upload className="h-4 w-4" />
          {uploading ? t('uploading') : t('upload')}
        </Button>
        <p className="text-xs leading-relaxed text-muted-foreground">
          {t('uploadHint')}
        </p>
        {error && (
          <p role="alert" className="text-xs text-destructive">
            {t('uploadError')}
          </p>
        )}
        {appearance.icon === 'custom' ? (
          <p className="text-xs text-muted-foreground">{t('originalColors')}</p>
        ) : (
          appearance.icon !== 'none' && (
            <>
              <ColorControl
                label={t('iconColor')}
                value={appearance.iconColor}
                fallback="#f59e0b"
                onChange={(iconColor) => onChange({ iconColor })}
              />
              <div className="flex gap-2">
                {['#f59e0b', '#94a3b8', '#b87333'].map((iconColor, index) => (
                  <button
                    key={iconColor}
                    type="button"
                    aria-label={t(['gold', 'silver', 'bronze'][index])}
                    aria-pressed={appearance.iconColor === iconColor}
                    onClick={() => onChange({ iconColor })}
                    className="h-7 w-7 rounded-full border-2 border-background ring-1 ring-border focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
                    style={{ backgroundColor: iconColor }}
                  />
                ))}
              </div>
            </>
          )
        )}
        {appearance.icon !== 'none' && (
          <label className="grid gap-2 text-sm">
            <span className="flex justify-between">
              {t('iconSize')}
              <span className="text-xs tabular-nums text-muted-foreground">
                {appearance.iconSize}px
              </span>
            </span>
            <input
              type="range"
              min={24}
              max={64}
              step={4}
              value={appearance.iconSize}
              className="w-full accent-primary"
              onChange={(event) =>
                onChange({ iconSize: Number(event.target.value) })
              }
            />
          </label>
        )}
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="gap-2"
          onClick={() => {
            uploadVersion.current++
            setUploading(false)
            setError(false)
            onChange({
              icon: 'crown',
              iconColor: DEFAULT_APPEARANCE.iconColor,
              iconSize: 48,
              customIcon: null,
            })
          }}
        >
          <RotateCcw className="h-3.5 w-3.5" />
          {t('resetIcon')}
        </Button>
      </section>
      <details className="rounded-md border p-3">
        <summary className="cursor-pointer text-sm font-semibold">
          {t('foregroundTitle')}
        </summary>
        <div className="mt-4 space-y-4">
          <ColorControl
            label={t('textColor')}
            value={appearance.textColor}
            fallback="#0f172a"
            onChange={(textColor) => onChange({ textColor })}
          />
          <ColorControl
            label={t('connectorColor')}
            value={appearance.connectorColor}
            fallback="#64748b"
            onChange={(connectorColor) => onChange({ connectorColor })}
          />
          <ColorControl
            label={t('cardColor')}
            value={appearance.cardColor}
            fallback="#ffffff"
            onChange={(cardColor) => onChange({ cardColor })}
          />
          <label className="grid gap-2 text-sm">
            <span className="flex justify-between">
              {t('cardOpacity')}
              <span className="text-xs text-muted-foreground">
                {Math.round(appearance.cardOpacity * 100)}%
              </span>
            </span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={appearance.cardOpacity}
              className="w-full accent-primary"
              onChange={(event) =>
                onChange({ cardOpacity: Number(event.target.value) })
              }
            />
          </label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="gap-2"
            onClick={() =>
              onChange({
                textColor: null,
                connectorColor: null,
                cardColor: null,
                cardOpacity: 1,
              })
            }
          >
            <RotateCcw className="h-3.5 w-3.5" />
            {t('resetForeground')}
          </Button>
        </div>
      </details>
      {storageError && (
        <p role="status" className="text-xs text-muted-foreground">
          {t('storageError')}
        </p>
      )}
    </div>
  )
}
